import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { apiPost } from '@/lib/http/client';

import type { CardinalityConflict } from '../core/types';

interface AssociationInput {
  association_type_name: string;
  target_entity_id: string;
}

interface ValidateConflictsResponse {
  conflicts?: CardinalityConflict[];
}

export interface UseAssociationConflictsParams {
  orgId: number | string | undefined;
  sourceEntityType: string;
  sourceEntityId: string;
  associations: AssociationInput[];
}

export interface UseAssociationConflictsResult {
  conflicts: CardinalityConflict[];
  isChecking: boolean;
  error: Error | null;
  acknowledged: boolean;
  acknowledge: () => void;
  allResolved: boolean;
}

const DEBOUNCE_MS = 300;

/**
 * Debounced, race-safe, fail-closed hook for pre-validating association
 * cardinality conflicts before a user submits a CRM event batch.
 *
 * Validation fires 300 ms after the sorted target-id set changes.
 * Out-of-order responses are discarded via latestRequestId.
 * On network error the gate stays CLOSED (allResolved=false) so the user
 * falls through to the submit-time block rather than silently bypassing.
 *
 * Acknowledgement is keyed to the stable sorted target-id string so
 * re-rendering with the same association set does not reset a prior ack.
 */
export function useAssociationConflicts({
  orgId,
  sourceEntityType,
  sourceEntityId,
  associations,
}: UseAssociationConflictsParams): UseAssociationConflictsResult {
  // Stable key: re-renders that produce a new array reference but identical
  // sorted IDs map to the same string, preventing spurious debounce triggers.
  const sortedKey = useMemo(
    () =>
      associations
        .filter(a => a.target_entity_id)
        .map(a => `${a.association_type_name}\x01${a.target_entity_id}`)
        .sort()
        .join('\x00'),
    [associations]
  );

  // Always-current ref so the debounce timer can access the latest associations
  // without adding the unstable array to the effect dependency list.
  const associationsRef = useRef(associations);
  associationsRef.current = associations;

  const [conflicts, setConflicts] = useState<CardinalityConflict[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  // Stores the sortedKey that was most recently acknowledged; null = never acked.
  const [ackedKey, setAckedKey] = useState<string | null>(null);

  const latestRequestId = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!orgId || !sourceEntityId || sortedKey === '') {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      setConflicts([]);
      setIsChecking(false);
      setError(null);
      return;
    }

    setIsChecking(true);

    if (debounceTimer.current !== null) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      latestRequestId.current += 1;
      const requestId = latestRequestId.current;

      const now = new Date().toISOString();
      // Drop entries with an empty target_entity_id — the sortedKey already
      // ignores them, but the events payload must too. Sending an empty
      // target_entity_id makes the backend reject the batch, which the
      // fail-closed error handler then treats as an unresolved conflict,
      // blocking the user from submitting a perfectly valid form.
      const currentAssociations = associationsRef.current.filter(
        a => a.target_entity_id
      );
      const events = currentAssociations.map((assoc, idx) => ({
        entity_id: sourceEntityId,
        entity_type_name: sourceEntityType,
        event_type_name: 'ahoy_association_added',
        seq: idx + 1,
        occurred_at: now,
        source_type_name: 'ahoy_user',
        source_id: 'frontend-validate',
        metadata: {},
        payload: {
          target_entity_id: assoc.target_entity_id,
          association_type_name: assoc.association_type_name,
        },
      }));

      apiPost<ValidateConflictsResponse>(
        `/api/orgs/${orgId}/crm-events/associations/validate/`,
        { events }
      )
        .then(response => {
          if (requestId !== latestRequestId.current) {
            return;
          }
          setConflicts(response.conflicts ?? []);
          setIsChecking(false);
          setError(null);
        })
        .catch(err => {
          if (requestId !== latestRequestId.current) {
            return;
          }
          // Fail-closed: surface the error and keep allResolved=false so the
          // user falls back to the submit-time enforcement block.
          setError(err instanceof Error ? err : new Error(String(err)));
          setIsChecking(false);
        });
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimer.current !== null) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      // Discard any in-flight request for the previous key.
      latestRequestId.current += 1;
    };
  }, [sortedKey, orgId, sourceEntityId, sourceEntityType]);

  // Key acknowledgement to the current sorted target-id set so the same
  // conflict set does not lose its ack on re-render or re-validation.
  const acknowledge = useCallback(() => {
    setAckedKey(sortedKey);
  }, [sortedKey]);

  const acknowledged = sortedKey !== '' && ackedKey === sortedKey;

  // Gate: open only when not mid-flight, no error, and either no conflicts or
  // the user has explicitly acknowledged the current conflict set.
  const allResolved = useMemo(
    () =>
      !isChecking && error === null && (conflicts.length === 0 || acknowledged),
    [isChecking, error, conflicts.length, acknowledged]
  );

  return useMemo(
    () => ({
      conflicts,
      isChecking,
      error,
      acknowledged,
      acknowledge,
      allResolved,
    }),
    [conflicts, isChecking, error, acknowledged, acknowledge, allResolved]
  );
}
